import { useState, useCallback } from 'react';
import { ApiUrls } from '@ideoz/core';

export interface UploadedAttachment {
  id: string;
  file: File;
  preview?: string;
  isImage: boolean;
  isUploading: boolean;
  uploadProgress?: number;
  error?: string;
}

export interface UseFileAttachmentsOptions {
  maxFiles?: number;
  allowedFileTypes?: string[];
  maxFileSize?: number; // File size limit in bytes
  maxTotalSize?: number; // Total size limit for all files in bytes
  onUploadComplete?: (attachmentId: string) => void;
  onUploadError?: (error: string) => void;
}

export interface UseFileAttachmentsReturn {
  attachments: UploadedAttachment[];
  isUploading: boolean;
  addFiles: (files: FileList | File[]) => Promise<void>;
  removeFile: (fileId: string) => Promise<void>;
  clearAttachments: () => void;
  getAttachmentIds: () => string[];
}

/**
 * Hook for managing file attachments with upload functionality
 * Shared across web and plugin platforms
 */
export const useFileAttachments = ({
  maxFiles = 10,
  maxFileSize = 102400, // 100KB limit
  maxTotalSize = 102400, // 100KB total limit
  allowedFileTypes = [
    'text/plain',
    'text/markdown',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ],
  onUploadComplete,
  onUploadError,
}: UseFileAttachmentsOptions): UseFileAttachmentsReturn => {
  // Upload endpoint is the same for both web and plugin
  const uploadEndpoint = `${ApiUrls.chatAttachments}`;
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});

  // Generate unique ID for files
  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Calculate current total size of attachments
  const getCurrentTotalSize = useCallback(() => {
    return attachments.reduce((total, att) => total + att.file.size, 0);
  }, [attachments]);

  // Check if file type is allowed
  const isFileTypeAllowed = useCallback((file: File) => {
    const fileExtension = file.name.toLowerCase().split('.').pop();
    return allowedFileTypes.includes(file.type) || 
           (fileExtension === 'md' && (file.type === '' || file.type === 'text/plain')) ||
           (fileExtension === 'txt' && file.type === '') ||
           (fileExtension === 'pptx' && file.type === '') ||
           (fileExtension === 'ppt' && file.type === '') ||
           (fileExtension === 'xlsx' && file.type === '') ||
           (fileExtension === 'xls' && file.type === '') ||
           (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '') && file.type.startsWith('image/'));
  }, [allowedFileTypes]);

  // Check if file size is within limit
  const isFileSizeAllowed = useCallback((file: File) => {
    return file.size <= maxFileSize;
  }, [maxFileSize]);

  // Check if file is not empty
  const isFileNotEmpty = useCallback((file: File) => {
    return file.size > 0;
  }, []);

  // Check if file is an image
  const isImageFile = (file: File) => {
    return file.type.startsWith('image/');
  };

  // Create file preview for images
  const createFilePreview = async (file: File): Promise<string | undefined> => {
    if (isImageFile(file)) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });
    }
    return undefined;
  };

  // Upload single file to API using fetch with progress simulation
  const uploadFile = async (file: File, tempId: string): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    // Simulate upload progress
    const simulateProgress = () => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 15;
        if (progress >= 90) {
          progress = 90;
          clearInterval(interval);
        }
        
        setUploadProgress(prev => ({ ...prev, [tempId]: Math.round(progress) }));
        setAttachments(prev => prev.map(att => 
          att.id === tempId ? { ...att, uploadProgress: Math.round(progress) } : att
        ));
      }, 100);
      
      return interval;
    };

    const progressInterval = simulateProgress();

    try {
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          // Use generic message
        }
        
        if (response.status === 413) {
          errorMessage = `File "${file.name}" is too large. Please compress it and try again.`;
        } else if (response.status === 415) {
          errorMessage = `File type of "${file.name}" is not supported.`;
        } else if (response.status === 400) {
          errorMessage = `Invalid file "${file.name}". Please check the file and try again.`;
        } else if (response.status >= 500) {
          errorMessage = `Server error while uploading "${file.name}". Please try again later.`;
        }
        
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      const attachmentId = responseData.id || responseData.attachmentId;
      
      if (!attachmentId) {
        throw new Error('No attachment ID received from server');
      }

      // Update attachment with server ID and complete progress
      setAttachments(prev => prev.map(att => 
        att.id === tempId 
          ? { ...att, id: attachmentId, isUploading: false, uploadProgress: 100 }
          : att
      ));
      
      // Clean up progress tracking
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[tempId];
        return newProgress;
      });

      onUploadComplete?.(attachmentId);
      return attachmentId;

    } catch (error) {
      clearInterval(progressInterval);
      
      // Mark attachment as failed
      setAttachments(prev => prev.map(att => 
        att.id === tempId 
          ? { ...att, isUploading: false, error: error instanceof Error ? error.message : 'Upload failed' }
          : att
      ));

      throw error instanceof Error ? error : new Error('Upload failed');
    }
  };

  // Delete file from server
  const deleteFile = async (attachmentId: string): Promise<void> => {
    try {
      const response = await fetch(`${uploadEndpoint}${attachmentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      throw error;
    }
  };

  // Add files and start upload
  const addFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    // Separate files by validation status
    const emptyFiles = fileArray.filter(file => !isFileNotEmpty(file));
    const oversizedFiles = fileArray.filter(file => isFileNotEmpty(file) && !isFileSizeAllowed(file));
    const validFiles = fileArray.filter(file => 
      isFileNotEmpty(file) && isFileTypeAllowed(file) && isFileSizeAllowed(file)
    );
    const invalidTypeFiles = fileArray.filter(file => 
      isFileNotEmpty(file) && !isFileTypeAllowed(file) && isFileSizeAllowed(file)
    );

    // Check total size limit
    const currentTotalSize = getCurrentTotalSize();
    const maxTotalSizeKB = Math.round(maxTotalSize / 1024);
    let filesToProcess = [...validFiles];
    let rejectedFiles: File[] = [];
    let totalSizeWithNewFiles = currentTotalSize;
    
    // Filter files that would exceed total size limit
    const acceptedFiles: File[] = [];
    for (const file of validFiles) {
      if (totalSizeWithNewFiles + file.size <= maxTotalSize) {
        acceptedFiles.push(file);
        totalSizeWithNewFiles += file.size;
      } else {
        rejectedFiles.push(file);
      }
    }
    
    filesToProcess = acceptedFiles;
    
    // Show error for files rejected due to total size limit
    if (rejectedFiles.length > 0) {
      const rejectedFileNames = rejectedFiles.map(f => f.name);
      const errorMessage = `${rejectedFiles.length} file${rejectedFiles.length > 1 ? 's' : ''} cannot be added. Total size limit of ${maxTotalSizeKB}KB would be exceeded: ${rejectedFileNames.join(', ')}`;
      onUploadError?.(errorMessage);
    }

    // Enhanced error messages with file details
    if (emptyFiles.length > 0) {
      const fileNames = emptyFiles.map(f => f.name);
      const errorMessage = `${emptyFiles.length} empty file${emptyFiles.length > 1 ? 's' : ''} cannot be uploaded. Please select files with content: ${fileNames.join(', ')}`;
      onUploadError?.(errorMessage);
    }

    if (oversizedFiles.length > 0) {
      const maxSizeKB = Math.round(maxFileSize / 1024);
      const fileNames = oversizedFiles.map(f => f.name);
      const errorMessage = `${oversizedFiles.length} file${oversizedFiles.length > 1 ? 's' : ''} exceeding ${maxSizeKB}KB and cannot be uploaded. Please compress them, then try again: ${fileNames.join(', ')}`;
      onUploadError?.(errorMessage);
    }

    if (invalidTypeFiles.length > 0) {
      const allowedTypesDisplay = ['PDF', 'DOC', 'DOCX', 'TXT', 'MD', 'PPT', 'PPTX', 'XLS', 'XLSX', 'Images'];
      const fileNames = invalidTypeFiles.map(f => f.name);
      const errorMessage = `${invalidTypeFiles.length} file${invalidTypeFiles.length > 1 ? 's' : ''} with unsupported format${invalidTypeFiles.length > 1 ? 's' : ''} cannot be uploaded. Allowed types: ${allowedTypesDisplay.join(', ')}: ${fileNames.join(', ')}`;
      onUploadError?.(errorMessage);
    }

    if (filesToProcess.length === 0) {
      return;
    }

    // Check max files limit
    if (attachments.length + filesToProcess.length > maxFiles) {
      const remainingSlots = maxFiles - attachments.length;
      const excessFiles = filesToProcess.slice(remainingSlots);
      const excessFileNames = excessFiles.map(f => f.name);
      const errorMessage = `Maximum ${maxFiles} files allowed. ${excessFiles.length} file${excessFiles.length > 1 ? 's' : ''} cannot be added: ${excessFileNames.join(', ')}`;
      onUploadError?.(errorMessage);
      
      filesToProcess.splice(remainingSlots);
      if (filesToProcess.length === 0) {
        return;
      }
    }

    // Create attachment objects with temporary IDs
    const newAttachments: UploadedAttachment[] = [];
    
    for (const file of filesToProcess) {
      const tempId = generateFileId();
      const preview = await createFilePreview(file);
      
      newAttachments.push({
        id: tempId,
        file,
        preview,
        isImage: isImageFile(file),
        isUploading: true,
        uploadProgress: 0
      });
    }

    // Add to state immediately to show loading
    setAttachments(prev => [...prev, ...newAttachments]);

    // Start uploads
    for (const attachment of newAttachments) {
      try {
        await uploadFile(attachment.file, attachment.id);
      } catch (error) {
        // Mark attachment as failed
        setAttachments(prev => prev.map(att => 
          att.id === attachment.id 
            ? { ...att, isUploading: false, error: error instanceof Error ? error.message : 'Upload failed' }
            : att
        ));
        
        const errorMessage = `Failed to upload "${attachment.file.name}": ${error instanceof Error ? error.message : 'Upload failed'}`;
        onUploadError?.(errorMessage);
      }
    }
  }, [attachments.length, maxFiles, maxFileSize, maxTotalSize, isFileTypeAllowed, isFileSizeAllowed, isFileNotEmpty, getCurrentTotalSize, onUploadComplete, onUploadError, uploadEndpoint]);

  // Remove file (both from state and server)
  const removeFile = useCallback(async (fileId: string) => {
    const attachment = attachments.find(att => att.id === fileId);
    
    if (attachment) {
      // If file is still uploading, just remove it from state
      if (attachment.isUploading) {
        setAttachments(prev => prev.filter(att => att.id !== fileId));
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
        return;
      }

      // If file is uploaded, delete from server first
      try {
        await deleteFile(fileId);
        setAttachments(prev => prev.filter(att => att.id !== fileId));
      } catch (error) {
        // Still remove from UI even if server delete fails
        setAttachments(prev => prev.filter(att => att.id !== fileId));
        
        const fileName = attachment.file.name;
        const errorMessage = `Failed to delete "${fileName}" from server: ${error instanceof Error ? error.message : 'Delete failed'}. File removed from interface.`;
        onUploadError?.(errorMessage);
        throw error;
      }
    }
  }, [attachments, onUploadError, uploadEndpoint]);

  // Clear all attachments
  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setUploadProgress({});
  }, []);

  // Check if any files are currently uploading
  const isUploading = attachments.some(att => att.isUploading);

  // Get array of uploaded attachment IDs
  const getAttachmentIds = useCallback(() => {
    return attachments
      .filter(att => !att.isUploading && !att.error)
      .map(att => att.id);
  }, [attachments]);

  return {
    attachments,
    isUploading,
    addFiles,
    removeFile,
    clearAttachments,
    getAttachmentIds
  };
};
